/**
 * Emergency call, from every patient page (the app header). One tap opens the call sheet — the
 * confirmation step — with the primary contact first and the others right below for quick access;
 * "Call" hands the number to the phone's dialer, where the patient presses call. No contacts yet:
 * the sheet links to the page that adds them.
 */

import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { Link } from "react-router-dom";

import { emergencyContactsKey, listEmergencyContacts, type EmergencyContact } from "../api/emergencyContacts";
import { useAuth } from "../auth/AuthContext";
import { formatPhone, onDial, telHref } from "../lib/dial";
import { relationshipText } from "../lib/emergencyContacts";
import { Modal } from "./ui/Modal";

export function PhoneIcon({ className = "h-4 w-4" }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.9} strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden="true">
      <path d="M22 16.9v3a2 2 0 0 1-2.2 2 19.8 19.8 0 0 1-8.6-3.1 19.5 19.5 0 0 1-6-6A19.8 19.8 0 0 1 2.1 4.2 2 2 0 0 1 4.1 2h3a2 2 0 0 1 2 1.7c.1.9.4 1.8.7 2.7a2 2 0 0 1-.5 2.1L8 9.8a16 16 0 0 0 6 6l1.3-1.3a2 2 0 0 1 2.1-.5c.9.3 1.8.6 2.7.7a2 2 0 0 1 1.7 2Z" />
    </svg>
  );
}

export function EmergencyButton() {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-haspopup="dialog"
        aria-expanded={open}
        className="inline-flex items-center gap-1.5 rounded-full bg-danger px-3 py-1.5 text-xs font-semibold text-white shadow-xs transition hover:opacity-90 sm:text-sm"
      >
        <PhoneIcon />
        <span className="sm:hidden">SOS</span>
        <span className="max-sm:hidden">Emergency</span>
      </button>
      <EmergencyCallDialog open={open} onClose={() => setOpen(false)} />
    </>
  );
}

/** The call sheet. Opening it is the confirmation step, so each "Call" goes straight to the
    dialer; the full number is shown, so a desktop user can read it out to their phone. */
export function EmergencyCallDialog({
  open,
  onClose,
  only,
}: {
  open: boolean;
  onClose: () => void;
  /** Ask about this one contact (the contacts page's Call) instead of listing all. */
  only?: EmergencyContact;
}) {
  const { user } = useAuth();
  const { data: contacts, isLoading } = useQuery({
    queryKey: emergencyContactsKey(user?.id),
    queryFn: listEmergencyContacts,
    enabled: open && !only,
  });
  const list = only ? [only] : contacts ?? [];
  const [first, ...others] = list;

  return (
    <Modal open={open} title={only ? `Call ${only.name}?` : "Emergency call"} onClose={onClose}>
      {isLoading && !only ? (
        <div className="space-y-3">
          <div className="skeleton h-24 rounded-2xl" />
          <div className="skeleton h-14 rounded-xl" />
        </div>
      ) : !first ? (
        <div className="space-y-3 text-center">
          <p className="text-sm text-ink-soft">You haven't saved an emergency contact yet.</p>
          <Link to="/patient/emergency-contacts" onClick={onClose} className="btn-primary">
            Add an emergency contact
          </Link>
        </div>
      ) : (
        <div className="space-y-4">
          <div className="rounded-2xl border border-danger/30 bg-danger-soft p-4">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <span className="truncate text-base font-semibold text-ink">{first.name}</span>
                  {first.is_primary && <span className="badge-danger">Primary</span>}
                </div>
                <div className="text-sm text-ink-soft">{relationshipText(first)}</div>
                <div className="mt-1 font-mono text-sm text-ink">{formatPhone(first.phone)}</div>
              </div>
            </div>
            <a
              href={telHref(first.phone)}
              onClick={onDial(first.phone)}
              className="mt-3 flex w-full items-center justify-center gap-2 rounded-xl bg-danger px-4 py-3 text-base font-semibold text-white shadow-xs transition hover:opacity-90"
            >
              <PhoneIcon className="h-5 w-5" />
              Call {first.name}
            </a>
          </div>

          {others.length > 0 && (
            <div>
              <div className="section-title mb-2">Or call</div>
              <ul className="divide-y divide-line rounded-xl border border-line">
                {others.map((c) => (
                  <li key={c.id} className="flex items-center justify-between gap-3 px-4 py-3">
                    <div className="min-w-0">
                      <div className="truncate text-sm font-medium text-ink">{c.name}</div>
                      <div className="text-xs text-ink-muted">
                        {relationshipText(c)} · <span className="font-mono">{formatPhone(c.phone)}</span>
                      </div>
                    </div>
                    <a href={telHref(c.phone)} onClick={onDial(c.phone)} className="btn-secondary btn-sm shrink-0">
                      <PhoneIcon className="h-3.5 w-3.5" />
                      Call
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <p className="text-xs text-ink-muted">
            Your phone's dialer opens with the number; press call there. In a life-threatening emergency, dial 112.
          </p>
          {!only && (
            <div className="text-right">
              <Link to="/patient/emergency-contacts" onClick={onClose} className="link text-sm">
                Manage emergency contacts
              </Link>
            </div>
          )}
        </div>
      )}
    </Modal>
  );
}
